import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2, MapPin, Phone, User, Award } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import { downloadCert } from "@/components/cert/CertRow";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const KIND = { flyer: "Flyer", card: "Visiting card", post: "Ad post" };

export default function MyDesigns() {
  const [data, setData] = useState({ items: [], me: {} });
  const [cert, setCert] = useState(null);
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/ai/member-designs").then((r) => setData(r.data)).catch(() => {}); api.get("/my-certificate").then((r) => setCert(r.data.cert)).catch(() => {}); }, []);

  const download = async (c) => {
    setBusy(c.id);
    try {
      const r = await api.get(`/ai/images/${c.id}/branded.png`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bengal-umrah-house-${c.kind}.jpg`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      a.remove();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Download failed"); }
    setBusy("");
  };
  const { me } = data;

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="my-designs-page">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold">My Flyers & Certificate</h1>
        <p className="text-sm text-emerald-100/60 mt-1">Designs from Bengal Umrah House. Each download adds your own name, number and address at the bottom.</p>
      </div>
      <Card className="glass border-gold/20 p-4 flex flex-wrap gap-x-6 gap-y-2 text-sm" data-testid="my-designs-details">
        <span className="flex items-center gap-1.5"><User className="w-4 h-4 text-gold" /> {me.name || "—"}</span>
        <span className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-gold" /> {me.phone || <em className="text-red-300">No number</em>}</span>
        <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-gold" /> {me.address || <em className="text-red-300">No address</em>}</span>
        <Link to="/profile" className="text-gold underline ml-auto" data-testid="my-designs-edit-profile">Edit in My Profile</Link>
      </Card>
      {cert && (
        <Card className="glass border-gold/40 p-4 flex flex-wrap items-center gap-3" data-testid="my-certificate-card">
          <Award className="w-8 h-8 text-gold" />
          <div className="flex-1"><p className="font-serif font-semibold text-gold">My Franchise Certificate</p><p className="text-xs text-emerald-100/60">{cert.cert_no} · {cert.area}</p></div>
          <Button size="sm" onClick={() => downloadCert(user.id, "pdf", me.name)} data-testid="my-cert-pdf" className="bg-gold text-[#0a2e22] hover:bg-gold/90">Download PDF</Button>
          <Button size="sm" variant="outline" onClick={() => downloadCert(user.id, "png", me.name)} data-testid="my-cert-png">Download image</Button>
        </Card>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="my-designs-grid">
        {data.items.map((c) => (
          <Card key={c.id} className="glass border-gold/20 overflow-hidden" data-testid={`my-design-${c.id}`}>
            <img src={`${API}/ai/images/${c.id}.png`} alt={KIND[c.kind]} className="w-full aspect-square object-contain bg-black/40" />
            <div className="p-3 flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-gold">{c.label || KIND[c.kind] || c.kind}</span>
              <Button size="sm" onClick={() => download(c)} disabled={busy === c.id} data-testid={`my-design-download-${c.id}`} className="bg-gold text-[#0a2e22] hover:bg-gold/90 gap-1.5">
                {busy === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download with my details
              </Button>
            </div>
          </Card>
        ))}
        {!data.items.length && <p className="text-sm text-emerald-100/50" data-testid="my-designs-empty">No designs shared yet. The Company will add flyers and cards here.</p>}
      </div>
    </div>
  );
}
